import {
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';

import { Loader } from 'components/organization';

import { useAuth, useSelectedLocation } from 'contexts';
import useTimeZone from 'hooks/use-timezone';
import { Page } from 'types';
import { Filter, FilterOperator, FilterState, filtersToRsqlString, LogicalOperator } from 'utils';
import { DataTableRef } from './data-table';
import {
  BaseDataType,
  ControlledDataTableColumn,
  FetchCsvArgs,
  FetchDataArgs,
  SortDirection,
  useDataTableContext
} from './data-table-context';
import './data-table.scss';

const FIELD_NAME_MAP: Record<string, string> = {
  booking: 'booking.bookingNumber',
  location: 'location.name',
  truckingCompany: 'truckingCompany.name'
};

const isMappedField = (field: any): field is keyof typeof FIELD_NAME_MAP => field in FIELD_NAME_MAP;

export interface ControlledDataTableProps<T extends BaseDataType> {
  checkboxSelection?: boolean;
  columns: ControlledDataTableColumn<T>[];
  // fetchCsv: (args: FetchCsvArgs) => Promise<void>;
  fetchCsv?: (
    columns: FetchCsvArgs['columns'],
    filterString?: FetchCsvArgs['filterString'],
    sortField?: FetchCsvArgs['sortField'],
    sortDirection?: FetchCsvArgs['sortDirection'],
    headers?: FetchCsvArgs['headers']
  ) => Promise<void>;
  fetchData: (args: FetchDataArgs) => Promise<Page<T>>;
  filterByLocation?: boolean;
  from?: Date;
  handleDelete?: (row: T) => void;
  handleEdit?: (row: T) => void;
  pageSizeOptions?: number[];
  dataTableRef?: RefObject<DataTableRef>;
  renderCell?: (column: ControlledDataTableColumn<T>, row: T) => ReactNode;
  searchLogicalOperator?: LogicalOperator;
  tableToolbar?: boolean;
  to?: Date;
}

const ControlledDataTable = <T extends BaseDataType>({
  checkboxSelection,
  children,
  columns,
  dataTableRef,
  fetchCsv,
  fetchData,
  filterByLocation,
  from,
  handleDelete,
  handleEdit,
  pageSizeOptions = [25, 50, 100],
  renderCell,
  searchLogicalOperator,
  tableToolbar,
  to
}: PropsWithChildren<ControlledDataTableProps<T>>) => {
  const { userProfile } = useAuth();
  const { selectedLocation } = useSelectedLocation();
  const { newZonedDayJs } = useTimeZone();

  const {
    data,
    setData,
    columnVisibility,
    filterState,
    setFilterState,
    loading,
    setLoading,
    pagination,
    setPagination,
    rowSelection,
    setRowSelection,
    searchInput,
    setSearchInput
  } = useDataTableContext<T>();

  const visibleColumns: ControlledDataTableColumn<T>[] = useMemo(() => {
    return columns
      .filter(({ allowedRoles }) => !allowedRoles || allowedRoles.includes(userProfile.role))
      .filter(({ field }) => columnVisibility.get(field));
  }, [columns, columnVisibility, userProfile.role]);

  const searchFilterState: FilterState = useMemo(
    () => ({
      logicalOperator: LogicalOperator.OR,
      filters: searchInput.length
        ? visibleColumns
            .filter(({ skipQuickSearch }) => !skipQuickSearch)
            .map(({ field }) => (isMappedField(field) ? FIELD_NAME_MAP[field] : field))
            .map((field): Filter => ({ field, filterOperator: FilterOperator.CONTAINS, value: searchInput }))
        : []
    }),
    [visibleColumns, searchInput]
  );

  const lastRequestTime = useRef<number>(0);
  const handleFetch = useCallback(
    async (requestTime: number) => {
      setLoading(true);
      let filterString = filtersToRsqlString(filterState, FIELD_NAME_MAP);
      let searchString = filtersToRsqlString(searchFilterState, FIELD_NAME_MAP);

      if (filterString && searchString) {
        filterString = `(${searchString}${searchLogicalOperator === LogicalOperator.OR ? ',' : ';'}${filterString})`;
      } else if (searchString) {
        filterString = searchString;
      }

      if (filterByLocation && filterString) {
        filterString = `location.id==${selectedLocation?.id};${filterString}`;
      } else if (filterByLocation) {
        filterString = `location.id==${selectedLocation?.id}`;
      }

      const response = await fetchData({
        filterString,
        from,
        to,
        pageNum: pagination.pageNum,
        pageSize: pagination.pageSize,
        sortDirection: pagination.sortDirection,
        sortField: pagination.sortField
      });

      if (requestTime === lastRequestTime.current) {
        setData(response.content ?? []);
        setPagination(prevState => ({
          ...prevState,
          totalCount: response.totalElements ?? 0,
          totalPages: response.totalPages ?? 0
        }));
      }
      setLoading(false);
    },
    [
      fetchData,
      filterByLocation,
      filterState,
      from,
      to,
      pagination.pageNum,
      pagination.pageSize,
      pagination.sortDirection,
      pagination.sortField,
      searchFilterState,
      searchLogicalOperator,
      selectedLocation,
      setData,
      setLoading,
      setPagination
    ]
  );

  const fetchTimeout = useRef<NodeJS.Timeout>();
  const debouncedFetch = useCallback(() => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      lastRequestTime.current = performance.now();
      const startTime = lastRequestTime.current;
      handleFetch(startTime);
    }, 1000);
  }, [handleFetch]);

  useEffect(debouncedFetch, [debouncedFetch]);

  useImperativeHandle(dataTableRef, () => ({ forceFetch: debouncedFetch, setFilterState }), [
    debouncedFetch,
    setFilterState
  ]);

  return (
    <>
      {!!tableToolbar && (
        <div className="table-toolbar d-flex mb-2 justify-content-between">
          {!!searchLogicalOperator && (
            <div className="search">
              <input
                type="text"
                placeholder="Search"
                value={searchInput ?? ''}
                onChange={event => setSearchInput(event.target.value)}
                className="input-field"
                data-cy="data-table-search"
              />
              <div className="material-icons">
                <span className="left">search</span>
                <span className="right" onClick={() => setSearchInput('')}>
                  close
                </span>
              </div>
            </div>
          )}
          {children}
        </div>
      )}
      <Loader loading={loading} className="d-flex flex-column flex-fill overflow-hidden">
        <div className="table-container flex-fill">
          <table className={`custom-table controlled flex-fill ${loading ? 'table-loading' : ''}`}>
            <thead className="table-header text-uppercase">
              <tr>
                {checkboxSelection && (
                  <td>
                    <input
                      type="checkbox"
                      className="pointer"
                      checked={data.length === rowSelection.size}
                      onChange={event =>
                        event.target.checked
                          ? setRowSelection(data.reduce((map, row) => map.set(row.id, true), new Map()))
                          : setRowSelection(new Map())
                      }
                    />
                  </td>
                )}
                {visibleColumns.map(c => (
                  <td className="column-label" key={c.field}>
                    <div
                      className="header-content"
                      onClick={
                        c.sortable === false
                          ? undefined
                          : () =>
                              setPagination(prevState => ({
                                ...prevState,
                                sortDirection:
                                  prevState.sortField === c.field &&
                                  prevState.sortDirection === SortDirection.DESCENDING
                                    ? SortDirection.ASCENDING
                                    : SortDirection.DESCENDING,
                                sortField: c.field
                              }))
                      }
                      draggable
                    >
                      {c.label}
                      {c.sortable !== false && (
                        <div
                          className="material-icons"
                          style={{ opacity: pagination.sortField === c.field ? 1 : 0.25 }}
                        >
                          {pagination.sortField === c.field && pagination.sortDirection === SortDirection.DESCENDING
                            ? 'expand_more'
                            : 'expand_less'}
                        </div>
                      )}
                    </div>
                  </td>
                ))}
                {(!!handleDelete || !!handleEdit) && <td />}
              </tr>
            </thead>
            <tbody>
              {data.map(row => (
                <tr key={row.id} data-cy={`data-table-row-${row.id}`}>
                  {checkboxSelection && (
                    <td>
                      <input
                        type="checkbox"
                        key={rowSelection.size + '' + row.id}
                        checked={rowSelection.get(row.id)}
                        onChange={event => {
                          setRowSelection(prevState => new Map(prevState).set(row.id, event.target.checked));
                        }}
                      />
                    </td>
                  )}
                  {visibleColumns.map(col => (
                    <td key={`${col.field}-${row.id}`} className="p-relative" data-column={col.field}>
                      <span className="overflow">
                        {!!renderCell
                          ? renderCell(col, row)
                          : !!col.type?.includes('date')
                          ? !row[col.field]
                            ? null
                            : newZonedDayJs(row[col.field]).format(col.dateFormat ?? 'M/D/YY LT')
                          : !!col.renderCell
                          ? col.renderCell(row[col.field], row)
                          : !!col.getValueLabel
                          ? col.getValueLabel(row[col.field], row)
                          : row[col.field]}
                      </span>
                      <span className="tooltip">{col.getValueLabel?.(row[col.field], row) ?? row[col.field]}</span>
                    </td>
                  ))}
                  {(!!handleDelete || !!handleEdit) && (
                    <td id={`${row.id}-edit-delete`}>
                      <div className={`grid-${Number(!!handleDelete) + Number(!!handleEdit)}`}>
                        {!!handleEdit && (
                          <button type="button" className="btn-close" onClick={() => handleEdit(row)}>
                            <span className="material-icons">edit</span>
                          </button>
                        )}
                        {!!handleDelete && (
                          <button type="button" className="btn-close" onClick={() => handleDelete(row)}>
                            <span className="material-icons">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && data.length === 0 && (
            <div className="no-rows-message p-4">
              <span>No data to display in table</span>
            </div>
          )}
        </div>
        {!!pagination.totalCount && (
          <>
            <div className="pagination-container flex-shrink-0">
              Rows per page:
              <select
                value={pagination.pageSize}
                onChange={event => {
                  setPagination(prevState => ({ ...prevState, pageSize: +event.target.value, pageNum: 0 }));
                  setRowSelection(new Map());
                }}
              >
                {pageSizeOptions.map(num => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              {pagination.pageNum !== undefined && pagination.pageSize !== undefined && (
                <>
                  {`${pagination.pageNum * pagination.pageSize + 1}-${
                    pagination.pageNum * pagination.pageSize + data.length
                  } of ${pagination.totalCount}`}
                  <button
                    className="pagination-button"
                    disabled={(pagination.pageNum ?? 0) <= 0}
                    onClick={() => {
                      setPagination(prevState => ({
                        ...prevState,
                        pageNum: Math.max(0, (prevState.pageNum ?? 0) - 1)
                      }));
                    }}
                  >
                    <span className="material-icons">chevron_left</span>
                  </button>
                  <button
                    className="pagination-button"
                    disabled={(pagination.pageNum ?? 0) >= pagination.totalPages}
                    onClick={() =>
                      pagination.pageNum !== undefined &&
                      setPagination(prevState => ({
                        ...prevState,
                        pageNum: Math.min(prevState.totalPages, (prevState.pageNum ?? 0) + 1)
                      }))
                    }
                  >
                    <span className="material-icons">chevron_right</span>
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </Loader>
    </>
  );
};

export default ControlledDataTable;
